
import type { Result } from '@empathybroker/tsclient-play/dist/models/search/result'
import type { RequestStatus } from '@empathyco/x-components'
import { computed, defineComponent, ref } from 'vue'
import { mapActions } from 'vuex'
import ExplainExportAside from '../../components/explain/explain-export-aside.component.vue'
import ExplainFiltersAside from '../../components/explain/explain-filters-aside.component.vue'
import ExplainResultGrid from '../../components/explain/explain-result-grid.component.vue'
import ExplainResultsAside from '../../components/explain/explain-results-aside.component.vue'
import ExplainSearchHeader from '../../components/explain/explain-search/header.component.vue'
import ExplainSectionLayout from '../../components/explain/explain-section-layout.component.vue'
import { EpbButton } from '../../components/shared/epb-button'
import { EpbText } from '../../components/shared/epb-text'
import FieldValueSelect from '../../components/shared/field-value-select.component.vue'
import XExtraParams from '../../components/x/x-extra-params.component.vue'
import { useExplainBrowse } from '../../composables/explain/use-explain-browse'
import { useContextQuery } from '../../composables/use-context-query'
import { useEpb } from '../../composables/use-epb'
import { useExplainFilters } from '../../composables/use-explain-filters'
import { useExplainSectionsConfig } from '../../composables/use-explain-sections-config'
import { ExplainSectionEnum } from '../../enums'
import { isExplainRoute } from '../../router/routes/explain.routes'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'

export interface InputEvent extends Event {
  currentTarget: HTMLInputElement
}

export default defineComponent({
  name: 'ExplainSearch',
  components: {
    EpbButton,
    EpbText,
    ExplainExportAside,
    ExplainSearchHeader,
    ExplainResultGrid,
    ExplainResultsAside,
    ExplainFiltersAside,
    ExplainSectionLayout,
    FieldValueSelect,
    XExtraParams,
  },
  async beforeRouteLeave(to, from, next) {
    this.contextWatchDisabled = true

    if (!isExplainRoute(to)) {
      await this.clearQuery()
    }

    next()
  },
  props: {
    extraParamsComponent: {
      type: Object,
      required: false,
    },
  },
  setup() {
    const { $epb } = useEpb()
    const { query, clearQuery } = useContextQuery()

    const { currentSectionConfig } = useExplainSectionsConfig(ExplainSectionEnum.SEARCH)
    const { filters, showExplainFilters, showExplainFiltersAside, areFiltersValid } =
      useExplainFilters({
        areAllFiltersMandatory: currentSectionConfig.value.areAllFiltersMandatory,
      })

    const explainBrowse = useExplainBrowse()

    const isSearchBoxDisabled = computed(() => !areFiltersValid.value)

    return {
      asideCollapsed: ref(true),
      clearQuery,
      contextWatchDisabled: ref(false),
      ...explainBrowse,
      extraParams: ref(undefined),
      filters,
      filtersStartOpened: !areFiltersValid.value,
      isSearchBoxDisabled,
      messages: $epb.messages.explain,
      query,
      searchMode: ref(undefined),
      selectedResultIds: ref([]),
      showExplainFilters,
      showExplainFiltersAside,
    }
  },
  computed: {
    initial(): boolean {
      return this.$x.status.search === 'initial'
    },
    loading(): boolean {
      return this.$x.status.search === 'loading'
    },
    error(): boolean {
      return this.$x.status.search === 'error'
    },
    selectedResults(): Result[] {
      return this.$x.results.filter((result: Result) => this.selectedResultIds.includes(result.id))
    },
    headerExtraParams() {
      return {
        headerExtraParams: this.extraParams,
      }
    },
  },
  watch: {
    context: {
      handler(): void {
        if (!this.contextWatchDisabled) {
          this.selectedResultIds = []
        }
      },
      deep: true,
    },
    query: {
      handler(): void {
        if (!this.contextWatchDisabled) {
          // Updates the query in the URL
          this.$emit('contextChanged')
        }
      },
    },
    '$x.status.search': {
      handler(status: RequestStatus): void {
        if (status === 'error') {
          this.$emit('showError', this.$epb.utils.decodeErrorMessage({}))
        }
      },
    },
    '$x.results': {
      handler(results: Result[]): void {
        this.selectedResultIds = this.selectedResultIds.filter((selectedId: string) =>
          results.some(({ id }) => id === selectedId),
        )
      },
    },
  },
  methods: {
    ...mapActions(CONTEXT_CONSTS.moduleName, {
      setQuery: CONTEXT_CONSTS.actions.setQuery,
    }),
    openFilters(): void {
      this.$refs.filtersAside.open()
    },
    openDownloadResults(): void {
      this.$refs.exportAside.open()
    },
    onResultClick(resultId: string): void {
      if (this.loading) {
        return
      }
      if (!this.selectedResultIds.includes(resultId)) {
        this.selectedResultIds.push(resultId)
      } else {
        this.selectedResultIds.splice(this.selectedResultIds.indexOf(resultId), 1)
      }
    },
    removeSelectedResults(resultIds: string[]): void {
      this.selectedResultIds = this.selectedResultIds.filter(
        (resultId: string) => !resultIds.includes(resultId),
      )
    },
    changeMode(mode: string) {
      this.searchMode = mode.toLowerCase()
      this.$emit('contextChanged')
    },
  },
})
